import { useEffect, RefObject, VideoHTMLAttributes, createRef, memo } from "react";
import Hls, { Events, ErrorTypes } from "hls.js";

export interface HlsPlayerProps
  extends VideoHTMLAttributes<HTMLVideoElement> {
  playerRef?: RefObject<HTMLVideoElement>;
  src: string;
  isHls: boolean;
}

export const ReactHlsPlayer = memo(function ReactHlsPlayer(props: HlsPlayerProps) {
  const {
    playerRef = createRef<HTMLVideoElement>(),
    src,
    autoPlay,
    isHls,
    ...restProps
  } = props;

  useEffect(() => {
    if (!isHls) {
      return;
    }

    if (!Hls.isSupported()) {
      return;
    }

    if (playerRef.current === null) {
      return;
    }

    const newHls = new Hls({
      enableWorker: false,
    });

    newHls.on(Events.MANIFEST_PARSED, () => {
      if (autoPlay) {
        playerRef.current
          ?.play()
          .catch(() =>
            console.warn("Unable to autoplay")
          );
      }
    });

    newHls.on(Events.ERROR, function (event, data) {
      if (data.fatal) {
        switch (data.type) {
        case ErrorTypes.NETWORK_ERROR:
          newHls.startLoad();
          break;
        case ErrorTypes.MEDIA_ERROR:
          newHls.recoverMediaError();
          break;
        default:
          newHls.destroy();
        }
      }
    });

    newHls.loadSource(src);
    newHls.attachMedia(playerRef.current);

    return () => {
      newHls.destroy();
    };
  }, [playerRef, src, autoPlay, isHls]);

  if (Hls.isSupported() && isHls) {
    return <video ref={playerRef} {...restProps} />;
  }

  return <video ref={playerRef} src={src} autoPlay={autoPlay} {...restProps} />;
});
